import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { UpdatePetInformationContent } from './content'
import { UpdatePetInformationControls } from './controls'

export enum Input {
  Breed = 'breed',
  Gender = 'gender',
  BirthDate = 'birth_date',
  SizeCategory = 'size_category',
  CoatType = 'coat_type',
}

export const updatePetInformationSchema = z.object({
  [Input.Breed]: z.object({
    id: z.string(),
    value: z.string(),
  }, {
    required_error: 'Breed is required.',
  }),
  [Input.Gender]: z.string({ required_error: 'Gender is required.' }),
  [Input.BirthDate]: z.string().optional(),
  [Input.SizeCategory]: z.object({ id: z.string(), value: z.string() }).optional(),
  [Input.CoatType]: z.object({ id: z.string(), value: z.string() }).optional(),
})

export type UpdatePetInformationSchema = z.infer<typeof updatePetInformationSchema>

type UpdatePetInformationFormContextType = {
  onSubmit: (values: UpdatePetInformationSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const UpdatePetInformationFormContext = createContext<UpdatePetInformationFormContextType | undefined>(undefined)

type UpdatePetInformationFormProps = UpdatePetInformationFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<UpdatePetInformationSchema>,
}

export function UpdatePetInformationForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: UpdatePetInformationFormProps) {

  const context: UpdatePetInformationFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <UpdatePetInformationFormContext.Provider value={context}>
      <Form<UpdatePetInformationSchema, typeof updatePetInformationSchema>
        schema={updatePetInformationSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </UpdatePetInformationFormContext.Provider>
  )
}

export function useUpdatePetInformationForm() {
  const context = useContext(UpdatePetInformationFormContext)
  if (context === undefined) {
    throw new Error('useUpdatePetInformationForm must be used within a UpdatePetInformationFormProvider')
  }
  return context
}

// Compound components
UpdatePetInformationForm.Content = UpdatePetInformationContent
UpdatePetInformationForm.Controls = UpdatePetInformationControls
